import React, { useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  REVIEW_PERMIT_APPLICATION,
  ReviewPermitApplicationData,
  ReviewPermitApplicationVars,
  PermitDocumentReviewInput,
} from "common/graphql";
import { Permit } from "common/types/permits";
import { OperatorInformationSection } from "../shared/OperatorInformationSection";
import { VehiclesListSection } from "../shared/VehiclesListSection";
import { DocumentsListSection } from "../shared/DocumentsListSection";
import DenyApplicationDialog from "./DenyApplicationDialog";
import RequestInfoDialog from "./RequestInfoDialog";
import type { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export interface ApplicationReviewContentProps {
  permit: Permit;
  onActionComplete?: () => void;
}

export const ApplicationReviewContent: React.FC<ApplicationReviewContentProps> = ({ permit, onActionComplete }) => {
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [showRequestInfoDialog, setShowRequestInfoDialog] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const [reviewApplication, { loading: reviewing }] = useMutation<
    ReviewPermitApplicationData,
    ReviewPermitApplicationVars
  >(REVIEW_PERMIT_APPLICATION);

  const latestProposal = permit.documentProposals?.[0];
  const latestProposalId = latestProposal?.id;

  const allDocuments =
    latestProposal?.documents?.map((doc) => ({
      ...doc.document,
      reviewStatus: doc.reviewStatus,
    })) || [];

  const isAwaitingInfo = permit.applicationStatus === "awaiting_additional_info";
  const infoRequestMessage = latestProposal?.reviewComment;

  const isActionInProgress = reviewing;

  const handleApprove = useCallback(async () => {
    if (!latestProposalId) {
      return;
    }
    setActionError(null);

    try {
      await reviewApplication({
        variables: {
          action: "approve",
          permitId: permit.id,
          documentProposalId: latestProposalId,
        },
      });
      onActionComplete?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to approve application");
    }
  }, [permit.id, latestProposalId, reviewApplication, onActionComplete]);

  const handleDenyConfirm = useCallback(
    async (reason: string) => {
      if (!latestProposalId) {
        return;
      }
      setActionError(null);

      try {
        await reviewApplication({
          variables: {
            action: "deny",
            permitId: permit.id,
            documentProposalId: latestProposalId,
            comment: reason,
          },
        });
        setShowDenyDialog(false);
        onActionComplete?.();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Failed to deny application");
      }
    },
    [permit.id, latestProposalId, reviewApplication, onActionComplete],
  );

  const handleRequestInfoConfirm = useCallback(
    async (documentReviews: PermitDocumentReviewInput[], comment: string) => {
      if (!latestProposalId) {
        return;
      }
      setActionError(null);

      try {
        await reviewApplication({
          variables: {
            action: "request_information",
            permitId: permit.id,
            documentProposalId: latestProposalId,
            comment,
            documentReviews,
          },
        });
        setShowRequestInfoDialog(false);
        onActionComplete?.();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Failed to request additional information");
      }
    },
    [permit.id, latestProposalId, reviewApplication, onActionComplete],
  );

  return (
    <>
      {/* Action Error Alert */}
      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Info Request Alert */}
      {isAwaitingInfo && (
        <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Information Previously Requested
          </Typography>
          {infoRequestMessage && <Typography variant="body2">{infoRequestMessage}</Typography>}
        </Alert>
      )}

      {/* Operator Information */}
      <OperatorInformationSection
        companyName={permit.payerAccount?.organizationName || null}
        accountResponsible={permit.applicationSubmittedByUser?.fullName || null}
        email={permit.applicationSubmittedByUser?.email || null}
        phoneNumber={permit.applicationSubmittedByUser?.phoneNumber || null}
      />

      <Divider sx={{ my: 2 }} />

      {/* Application Details */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Application Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Permit Type
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {permit.permitType?.name || "\u2014"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Number of Vehicles
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {permit.vehicles?.length || 0}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Vehicles List */}
      {permit.vehicles && permit.vehicles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <VehiclesListSection vehicles={permit.vehicles} />
        </>
      )}

      {/* Documentation */}
      {allDocuments.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <DocumentsListSection documents={allDocuments} />
        </>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
        <Button
          size={isMobile ? "small" : undefined}
          variant="contained"
          color="error"
          onClick={() => setShowDenyDialog(true)}
          disabled={isActionInProgress}
        >
          Deny
        </Button>
        <Button
          variant="outlined"
          color="warning"
          size={isMobile ? "small" : undefined}
          onClick={() => setShowRequestInfoDialog(true)}
          disabled={isAwaitingInfo || isActionInProgress}
        >
          Request Info
        </Button>
        <LoadingButton
          variant="contained"
          size={isMobile ? "small" : undefined}
          color="success"
          loading={reviewing}
          onClick={handleApprove}
          disabled={isAwaitingInfo || isActionInProgress}
        >
          Approve
        </LoadingButton>
      </Box>

      {/* Sub-dialogs */}
      <DenyApplicationDialog
        open={showDenyDialog}
        onClose={() => setShowDenyDialog(false)}
        onConfirm={handleDenyConfirm}
      />

      <RequestInfoDialog
        open={showRequestInfoDialog}
        onClose={() => setShowRequestInfoDialog(false)}
        onConfirm={handleRequestInfoConfirm}
        documents={allDocuments}
      />
    </>
  );
};

export default ApplicationReviewContent;
